import { useLogError } from '@/src/common/hooks/useLogError'
import type { PaymentMethodsComponent } from '@/src/experiences/card-payments/providePaymentMethods'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { Api } from '@/src/store'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks'
import { routerBack } from '@/src/common/utils/routerBack'
import { Linking } from 'react-native'
import { type ComponentProps, useCallback, useEffect, useMemo } from 'react'
import { useEvent } from 'tamagui'
import { useCompletePlaidLink } from '@/src/common/hooks/usePlaidLink/useCompletePlaidLink'
import { usePlaidLink } from '@/src/common/hooks/usePlaidLink'
import * as MesaLib from '@acre-rewards/lib'

export function PlatformPaymentMethods({
  ToWrap,
  products,
}: {
  ToWrap: PaymentMethodsComponent
  products: typeof MesaLib.Plaid.Products.options
}) {
  const query = useCardAccount()
  const Alert = useAlert(PlatformPaymentMethods.name)
  const logError = useLogError()

  const firstCardAccount = query.data?.[0]

  const handleAbsentCardAccount = useEvent(() => {
    Alert.alert('No card account found', 'Please try again later', [
      { text: 'OK', onPress: () => void routerBack() },
      {
        text: 'Contact support',
        onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
      },
    ])
    logError('No card account found when attempting to access payment methods', query)
  })

  const stillNoCardAccountAfterLoading =
    !query.isUninitialized && !query.isLoading && !query.isFetching && !firstCardAccount

  useEffect(() => {
    stillNoCardAccountAfterLoading && handleAbsentCardAccount()
  }, [stillNoCardAccountAfterLoading, handleAbsentCardAccount])

  return firstCardAccount ? (
    <PlatformPaymentMethodsForCardAccount
      ToWrap={ToWrap}
      cardAccountId={firstCardAccount.id}
      products={products}
    />
  ) : (
    <ToWrap loading />
  )
}

function PlatformPaymentMethodsForCardAccount({
  ToWrap,
  cardAccountId,
  products,
}: {
  ToWrap: PaymentMethodsComponent
  products: typeof MesaLib.Plaid.Products.options
} & Parameters<typeof useCompletePlaidLink>[0]) {
  const query = Api.endpoints['GET /card/:cardAccountId/payments/methods'].useQuery({
    params: { cardAccountId },
  })

  const [deletePaymentProfile, { isLoading: isDeletingPaymentProfile }] =
    Api.endpoints['DELETE /card/:cardAccountId/payments/methods'].useMutation()

  const { openPlaidLink, loading: plaidLinkLoading } = usePlaidLink(
    products,
    useCompletePlaidLink({ cardAccountId }),
  )
  const Alert = useAlert(PlatformPaymentMethodsForCardAccount.name)
  const logError = useLogError()

  return (
    <ToWrap
      data={useMemo(
        () =>
          query.data
            ? ([
                ...(query.data.unverifiedPlaidAccounts?.map(
                  (unverifiedPlaidAccount) =>
                    ({
                      unverifiedPlaidAccount,
                      key: unverifiedPlaidAccount.id,
                      plaidAccountId: unverifiedPlaidAccount.id,
                      isVerified: false,
                      last4: `${unverifiedPlaidAccount.subtype || unverifiedPlaidAccount.type} - ${unverifiedPlaidAccount.mask}`,
                      name: unverifiedPlaidAccount.name,
                      products: unverifiedPlaidAccount.item.products || [],
                      status: unverifiedPlaidAccount.item.status,
                    }) as const,
                ) || []),
                ...query.data.profiles.map(
                  (profile) =>
                    ({
                      profile,
                      key: profile.id,
                      isVerified: true,
                      last4:
                        `${profile.plaidAccount?.subtype || profile.plaidAccount?.type || 'TODO: ascertain non-nullability of this field'} - ${profile.plaidAccount?.mask || 'TODO: ascertain non-nullability of this field'}` as const,
                      name:
                        profile.plaidAccount?.name ||
                        'TODO: ascertain non-nullability of this field',
                      products: profile.plaidAccount?.item.products || [],
                      status: profile.plaidAccount?.item.status || null,
                      profileId: profile.id,
                      plaidAccountId: profile.plaidAccount?.id,
                    }) as const,
                ),
              ] as const)
            : undefined,
        [query.data],
      )}
      loading={query.isUninitialized || query.isLoading || query.isFetching}
      closePaymentMethod={(paymentMethod) => {
        const body = paymentMethod.profileId
          ? {
              profileId: paymentMethod.profileId,
            }
          : paymentMethod.plaidAccountId
            ? {
                unverifiedPlaidAccountId: paymentMethod.plaidAccountId,
              }
            : undefined

        if (!body) {
          const error = new Error('No payment method ID provided')
          logError('Failed to delete payment profile', error)
          Alert.alert('Failed to delete payment profile', 'Please try again later.')
          return
        }

        void deletePaymentProfile({
          params: {
            cardAccountId,
          },
          body,
        }).then((response) => {
          if (response.error) {
            logError('Failed to delete payment profile', response.error)
            Alert.alert('Failed to delete payment profile', 'Please try again later.')
          } else {
            Alert.alert('Payment profile deleted', 'Your payment profile has been deleted.')
            void query.refetch()
          }
        })
      }}
      closingPaymentMethod={isDeletingPaymentProfile}
      openPlaidLink={useCallback<NonNullable<ComponentProps<typeof ToWrap>['openPlaidLink']>>(
        (paymentMethod) => {
          if (!paymentMethod) {
            return openPlaidLink()
          }

          if (!paymentMethod.plaidAccountId) return

          if (!paymentMethod.isVerified) {
            return openPlaidLink({ accountId: paymentMethod.plaidAccountId })
          }

          if (paymentMethod.status === 'UPDATE_REQUIRED') {
            return openPlaidLink({ accountId: paymentMethod.plaidAccountId })
          }
        },
        [openPlaidLink],
      )}
      plaidLinkLoading={plaidLinkLoading}
    />
  )
}
